import { supabase } from "@/integrations/supabase/client";

/**
 * Settling a trailer after an order reaches a terminal state.
 *
 * Historically only two code paths ever cleared `trailers.current_location_status
 * = 'in_transit'`: a yard check-in, and the driver app marking the last leg
 * delivered. Orders closed out any other way (dispatcher Edit Order → Delivered,
 * cancel, EAPI-driven delivery) left the trailer pinned to `in_transit` forever
 * — and for "endpoint" destinations that are not YMS-tracked there is no gate
 * staff to ever check it in. Those trailers dropped out of Yard Audit and the
 * checkout list entirely.
 *
 * This module is the single shared rule for that settlement.
 */

export type TrailerSettleAction = "skip" | "at_site" | "unknown";

export interface TrailerSettleDecision {
  action: TrailerSettleAction;
  locationId: string | null;
  /** Human-readable reason, for logging. */
  reason: string;
}

/**
 * Pure decision. Mirrors the driver-app rule:
 *  - trailer isn't `in_transit` → leave it alone (it was already checked in
 *    somewhere else; never downgrade a live yard placement).
 *  - destination is YMS-tracked and resolved → `at_site` at that yard.
 *  - otherwise → `unknown` with the location cleared. We do not fabricate an
 *    `at_site` row with a null location_id.
 */
export function decideTrailerSettlement(input: {
  currentStatus: string | null | undefined;
  destination: { locationId: string | null; trackInYms: boolean };
}): TrailerSettleDecision {
  if (input.currentStatus !== "in_transit") {
    return { action: "skip", locationId: null, reason: "trailer_not_in_transit" };
  }
  const { locationId, trackInYms } = input.destination;
  if (trackInYms && locationId) {
    return { action: "at_site", locationId, reason: "yms_destination" };
  }
  return { action: "unknown", locationId: null, reason: "non_yms_destination" };
}

/**
 * Resolve a yard location from an order/leg destination. Prefers the FK, falls
 * back to a street-address match.
 *
 * NOTE: `public.locations` is a GLOBAL table — it has no `tenant_id` column.
 * Never add a tenant filter here; doing so returns no row and silently drives
 * callers into the "non-YMS" branch.
 */
export async function resolveYardLocation(
  fkId: string | null | undefined,
  streetAddress: string | null | undefined,
): Promise<{ locationId: string | null; trackInYms: boolean }> {
  // Cast locally: chaining filters on `locations` triggers TS2589 (deep type
  // instantiation) in the generated supabase types. Runtime is identical.
  const sb = supabase as any;

  if (fkId) {
    const { data } = await sb
      .from("locations")
      .select("id, track_in_yms")
      .eq("id", fkId)
      .maybeSingle();
    if (data) return { locationId: data.id, trackInYms: !!data.track_in_yms };
  }
  if (streetAddress) {
    const { data } = await sb
      .from("locations")
      .select("id, track_in_yms")
      .eq("archived", false)
      .ilike("street_address", streetAddress)
      .maybeSingle();
    if (data) return { locationId: data.id, trackInYms: !!data.track_in_yms };
  }
  return { locationId: null, trackInYms: false };
}

export interface SettleTrailerInput {
  orderId: string;
  /** Human-readable trailer id (`trailers.trailer_id`). */
  trailerHumanId: string | null | undefined;
  tenantId?: string | null;
  /** `delivered` writes a checked_in event at a YMS yard; `canceled` never does. */
  outcome: "delivered" | "canceled";
  userId?: string | null;
}

export interface SettleTrailerResult {
  action: TrailerSettleAction;
  reason: string;
  locationId?: string | null;
}

/**
 * Settle the trailer attached to an order that just reached a terminal state.
 *
 * Best-effort: never throws — a settlement failure must not roll back an
 * otherwise successful order write.
 */
export async function settleTrailerAfterOrderClose({
  orderId,
  trailerHumanId,
  tenantId,
  outcome,
  userId,
}: SettleTrailerInput): Promise<SettleTrailerResult> {
  try {
    if (!trailerHumanId) return { action: "skip", reason: "no_trailer" };

    let trailerQuery = supabase
      .from("trailers")
      .select("id, current_location_status")
      .eq("trailer_id", trailerHumanId.trim());
    if (tenantId) trailerQuery = trailerQuery.eq("tenant_id", tenantId);
    const { data: trailer } = await trailerQuery.maybeSingle();
    if (!trailer) return { action: "skip", reason: "trailer_not_found" };

    // A canceled order never "arrives" anywhere — release the trailer to
    // unknown rather than inventing a yard placement.
    let destination = { locationId: null as string | null, trackInYms: false };
    if (outcome === "delivered") {
      const { data: legs } = await supabase
        .from("legs")
        .select("leg_sequence, ship_to_location_id, ship_to_street_address")
        .eq("order_id", orderId)
        .order("leg_sequence", { ascending: false })
        .limit(1);
      const lastLeg = legs?.[0] as any;
      if (lastLeg) {
        destination = await resolveYardLocation(
          lastLeg.ship_to_location_id ?? null,
          lastLeg.ship_to_street_address ?? null,
        );
      }
    }

    const decision = decideTrailerSettlement({
      currentStatus: (trailer as any).current_location_status,
      destination,
    });

    if (decision.action === "skip") {
      return { action: "skip", reason: decision.reason };
    }

    if (decision.action === "at_site") {
      await supabase.from("trailer_events").insert({
        trailer_id: trailer.id,
        event_type: "checked_in" as const,
        location_id: decision.locationId!,
        created_by_user_id: userId ?? null,
        notes: "Auto check-in on order delivery",
        ...(tenantId ? { tenant_id: tenantId } : {}),
      } as any);

      await supabase
        .from("trailers")
        .update({
          current_location_status: "at_site" as const,
          current_location_id: decision.locationId,
        })
        .eq("id", trailer.id);

      return { action: "at_site", reason: decision.reason, locationId: decision.locationId };
    }

    await supabase
      .from("trailers")
      .update({
        current_location_status: "unknown" as const,
        current_location_id: null,
      })
      .eq("id", trailer.id);

    return { action: "unknown", reason: decision.reason };
  } catch (err) {
    console.warn("[trailer-settle] failed to settle trailer after order close", {
      orderId,
      trailerHumanId,
      outcome,
      err,
    });
    return { action: "skip", reason: "threw" };
  }
}

/**
 * A trailer that has been `in_transit` this long without any yard event is
 * almost certainly a missed settlement, not a genuinely moving trailer.
 */
export const STALE_IN_TRANSIT_DAYS = 7;

export function isStaleInTransit(
  trailer: { current_location_status?: string | null; updated_at?: string | null },
  now: Date = new Date(),
  thresholdDays: number = STALE_IN_TRANSIT_DAYS,
): boolean {
  if (trailer?.current_location_status !== "in_transit") return false;
  if (!trailer.updated_at) return false;
  const t = new Date(trailer.updated_at).getTime();
  if (Number.isNaN(t)) return false;
  return now.getTime() - t > thresholdDays * 24 * 60 * 60 * 1000;
}
